import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import Modal from '@components/common/Modal';

import {
	countItems,
	generateDynamicOptions,
	generateListOfCheckedOptions,
	generateListOfCheckedOptionsWithCategory,
	generateOptions,
} from '@utils/groupSelectUtils';

import { IGroupSelectOptions, IGroupSelectProps } from '../GroupSelect.types';

import Meta from '../../Meta';
import CheckboxContent from '../CheckboxContent';
import GroupSelectMenu from '../GroupSelectMenu';

import '../GroupSelect.scss';

const GroupSelectSitesCells: React.FC<IGroupSelectProps> = ({
	checkboxLabel,
	defaultOptions,
	disableCheckbox,
	disabled,
	errorMessage,
	gap = '20px',
	iSortByChecked = true,
	isCheckboxTheme = false,
	isInLine = false,
	label,
	maxSelectionNumber,
	maxSelectionText,
	modalLabel,
	name,
	onChange,
	options = [],
	readOnly,
	required = false,
	searchPlaceholder,
	searchThreshold,
	selectAllName = 'all',
	setSelectedItemsNumber,
	withVirtualization = false,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [dynamicOptions, setDynamicOptions] = useState<Array<IGroupSelectOptions>>(() =>
		generateDynamicOptions(options, defaultOptions || []),
	);
	const [isAllItemsSelected, setIsAllItemsSelected] = useState(false);
	const [isSelectMenuOpen, setIsSelectMenuOpen] = useState(false);

	const getSelectedListOfOptions = useCallback(() => generateListOfCheckedOptions(dynamicOptions), [dynamicOptions]);

	const [selectedListOfOptions, setSelectedListOfOptions] = useState(() => getSelectedListOfOptions());

	useEffect(() => {
		setDynamicOptions(() => generateDynamicOptions(options, defaultOptions || []));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [options.length, defaultOptions]);

	const countAllOptions = useMemo(() => {
		return options.reduce<number>((acc, option) => {
			const itemCountInCategory = countItems(option.items);

			return acc + itemCountInCategory;
		}, 0);
	}, [options]);

	const isDisableOpenSelectMenu = (
		(isCheckboxTheme && !selectedListOfOptions.length) ||
		disabled ||
		disableCheckbox
	);

	const onChangeHandler = (options: Array<IGroupSelectOptions>) => {
		const checkedList: Array<IGroupSelectOptions> = generateListOfCheckedOptionsWithCategory(options);

		onChange(checkedList);
	};

	const removeAllOptions = () => {
		const uncheckedOptions = generateOptions(options, false);
		onChangeHandler(uncheckedOptions);
	};

	const addAllOptions = () => {
		const checkedOptions = generateOptions(options, true);
		onChangeHandler(checkedOptions);
	};

	useEffect(() => {
		const newSelectedOptions = getSelectedListOfOptions();

		setSelectedListOfOptions(newSelectedOptions);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dynamicOptions]);

	useEffect(() => {
		setIsAllItemsSelected(countAllOptions === selectedListOfOptions.length);

		if (setSelectedItemsNumber) setSelectedItemsNumber(selectedListOfOptions.length);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dynamicOptions, selectedListOfOptions]);

	const toggleSelectMenu = () => {
		if (isDisableOpenSelectMenu) {
			return;
		}

		setIsSelectMenuOpen((prevState) => !prevState);
	};

	return (
		<div
			ref={containerRef}
			className={cn('f5gGroupSelectContainer', {
				f5gGroupSelectContainerMinWidth: !isCheckboxTheme,
				f5gGroupSelectContainerInLine: isCheckboxTheme && isInLine,
			})}
			data-testid='group-select'
			style={{
				gap,
			}}
		>
			<CheckboxContent
				addAllOptions={addAllOptions}
				checkboxLabel={checkboxLabel}
				disabled={disableCheckbox}
				isInLine={isInLine}
				name={name}
				options={options}
				readOnly={readOnly}
				removeAllOptions={removeAllOptions}
				selectedListOfOptions={selectedListOfOptions}
			/>
			<div className='f5gGroupSelectControl'>
				<div
					className={cn({ f5gGroupSelectRequired: required && !!selectedListOfOptions.length })}
					data-testid='group-select-button'
					onClick={toggleSelectMenu}
				>
					<span
						className={cn('f5gGroupSelectButton', {
							f5gGroupSelectButtonDisabled: isDisableOpenSelectMenu,
							f5gGroupSelectButtonReadOnly: readOnly,
						})}
					>
						{label}
					</span>
				</div>
				&nbsp;
				{isCheckboxTheme && !!selectedListOfOptions.length && (
					<div>({isAllItemsSelected ? 'all' : selectedListOfOptions.length})</div>
				)}
			</div>
			{errorMessage && <Meta message={errorMessage} />}
			<Modal
				height='max-content'
				isOpen={isSelectMenuOpen}
				isScrollableContent
				maxHeight='80vh'
				onClose={toggleSelectMenu}
				title={modalLabel || label}
				width='480px'
			>
				<GroupSelectMenu
					defaultOptions={options}
					dynamicOptions={dynamicOptions}
					iSortByChecked={iSortByChecked}
					isAllItemsSelected={isAllItemsSelected}
					maxSelectionNumber={maxSelectionNumber}
					maxSelectionText={maxSelectionText}
					onSave={onChangeHandler}
					placeholder={searchPlaceholder}
					readOnly={readOnly}
					searchThreshold={searchThreshold}
					selectAllName={selectAllName}
					toggleSelectMenu={toggleSelectMenu}
					withVirtualization={withVirtualization}
				/>
			</Modal>
		</div>
	);
};

export default GroupSelectSitesCells;
